import React, { useState } from 'react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import logo from './assets/logo.png';

function Login({ onLogin }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [handicap, setHandicap] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      onLogin(userCredential.user);
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!name || !handicap) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, 'users', user.uid), {
        name: name,
        email: email,
        handicap: parseFloat(handicap),
        status: 'pending',
        role: 'member',
        createdAt: serverTimestamp(),
        uid: user.uid
      });

      onLogin(user);
    } catch (error) {
      console.error('Sign up error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters');
      } else {
        setError('Error creating account');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else {
        setError('Error sending reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsForgotPassword(false);
    setIsSignUp(false);
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setName('');
    setHandicap('');
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
        <div style={styles.logoSection}>
          <img src={logo} alt="KWAC Logo" style={styles.logo} />
        </div>

        <div style={styles.formSection}>
          {!isForgotPassword ? (
            <>
              <div style={styles.tabContainer}>
                <button
                  onClick={() => setIsSignUp(false)}
                  style={!isSignUp ? styles.tabActive : styles.tab}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setIsSignUp(true)}
                  style={isSignUp ? styles.tabActive : styles.tab}
                >
                  Sign Up
                </button>
              </div>

              {error && (
                <div style={styles.errorBox}>
                  ⚠️ {error}
                </div>
              )}

              <form onSubmit={isSignUp ? handleSignUp : handleLogin} style={styles.form}>
                {isSignUp && (
                  <>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Full Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Doe"
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Handicap</label>
                      <input
                        type="number"
                        step="0.1"
                        value={handicap}
                        onChange={(e) => setHandicap(e.target.value)}
                        placeholder="12.5"
                        style={styles.input}
                        min="-4"
                        max="54"
                        required
                      />
                    </div>
                  </>
                )}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={styles.input}
                    required
                  />
                </div>

                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    style={styles.forgotButton}
                  >
                    Forgot password?
                  </button>
                )}

                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
                </button>
              </form>

              {isSignUp && (
                <div style={styles.signupNote}>
                  <p style={styles.noteText}>
                    📋 Your account will be pending approval from a club administrator.
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h3 style={styles.forgotTitle}>Reset Password</h3>
              <p style={styles.forgotSubtitle}>
                Enter your email and we'll send you a password reset link.
              </p>

              {error && (
                <div style={styles.errorBox}>
                  ⚠️ {error}
                </div>
              )}

              {success && (
                <div style={styles.successBox}>
                  ✅ {success}
                </div>
              )}

              <form onSubmit={handleForgotPassword} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    style={styles.input}
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  style={styles.backToLoginButton}
                >
                  ← Back to Sign In
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <div style={styles.footer}>
        <p style={styles.footerText}>© 2026 KWAC by Joep Verploegen</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  loginCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '16px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
    width: '100%',
    maxWidth: '450px',
    overflow: 'hidden',
    border: '1px solid #2d2d2d'
  },
  logoSection: {
    background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
    padding: '50px 30px',
    textAlign: 'center',
    borderBottom: '1px solid #2d2d2d'
  },
  logo: {
    height: '120px',
    width: 'auto',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))'
  },
  formSection: {
    padding: '30px'
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '25px',
    backgroundColor: '#0f0f0f',
    borderRadius: '8px',
    padding: '5px',
    border: '1px solid #2d2d2d'
  },
  tab: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#9ca3af',
    transition: 'all 0.3s ease'
  },
  tabActive: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#d4af37',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#0f0f0f',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
  },
  errorBox: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '12px 15px',
    marginBottom: '20px',
    color: '#ef4444',
    fontSize: '14px',
    fontWeight: '600'
  },
  successBox: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #10b981',
    borderRadius: '8px',
    padding: '12px 15px',
    marginBottom: '20px',
    color: '#10b981',
    fontSize: '14px',
    fontWeight: '600'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  input: {
    padding: '14px 16px',
    fontSize: '16px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  forgotButton: {
    alignSelf: 'flex-end',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#d4af37',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '5px',
    marginTop: '-10px',
    textDecoration: 'underline',
    transition: 'all 0.3s ease'
  },
  submitButton: {
    padding: '16px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
  },
  backToLoginButton: {
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#9ca3af',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  signupNote: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    border: '1px solid #d4af37'
  },
  noteText: {
    fontSize: '13px',
    color: '#d4af37',
    margin: 0,
    textAlign: 'center',
    lineHeight: '1.5',
    fontWeight: '500'
  },
  forgotTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: '0 0 10px 0',
    textAlign: 'center',
    letterSpacing: '-0.01em'
  },
  forgotSubtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '0 0 25px 0',
    textAlign: 'center',
    lineHeight: '1.5',
    fontWeight: '400'
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '13px',
    color: '#d4af37',
    margin: 0,
    opacity: 0.8,
    fontWeight: '500'
  }
};

export default Login;